import { useMemo } from "react";
import {
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
} from "@mui/material";
import { IconCashBanknote } from "@tabler/icons-react";
import FelixCard from "../../../util/ui/felixCard";
import MoedaText from "../../../util/ui/inputs/moedaText";
import { DetalhePorMoeda } from "../../../../models/resultadoInternacional";

type Props = {
  detalhesPorMoeda?: { [moeda: string]: DetalhePorMoeda };
  rendimentosTotal?: number;
};

export default function RendimentosInternacionais({
  detalhesPorMoeda,
  rendimentosTotal = 0,
}: Props) {
  const rendimentosPorMoeda = useMemo(() => {
    if (!detalhesPorMoeda) return [];

    return Object.entries(detalhesPorMoeda)
      .filter(([_, detalhe]) => detalhe.rendimentosOriginal > 0)
      .map(([moeda, detalhe]) => ({
        moeda,
        valorOriginal: detalhe.rendimentosOriginal,
        valorBRL: detalhe.rendimentosBRL,
        percentual:
          rendimentosTotal > 0
            ? (detalhe.rendimentosBRL / rendimentosTotal) * 100
            : 0,
      }))
      .sort((a, b) => b.valorBRL - a.valorBRL);
  }, [detalhesPorMoeda, rendimentosTotal]);

  if (rendimentosPorMoeda.length === 0) {
    return null;
  }

  return (
    <FelixCard>
      <CardHeader
        title="Rendimentos Internacionais"
        avatar={<IconCashBanknote size={16} />}
      />
      <CardContent>
        <Table size="small">
          {/* Header mono uppercase tracked já vem do tema — sem Typography por dentro. */}
          <TableHead>
            <TableRow>
              <TableCell>Moeda</TableCell>
              <TableCell align="right">Valor Original</TableCell>
              <TableCell align="right">Valor (BRL)</TableCell>
              <TableCell align="right">% do Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rendimentosPorMoeda.map((item) => (
              <TableRow key={item.moeda}>
                <TableCell>
                  {/* Moeda é rótulo, não corpo de texto. */}
                  <Typography variant="labelMono" color="text.primary">
                    {item.moeda}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="dataMono" color="text.secondary">
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: item.moeda,
                      minimumFractionDigits: 2,
                    }).format(item.valorOriginal)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="dataMono" sx={{ fontWeight: 500 }}>
                    <MoedaText value={item.valorBRL} />
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="dataMono" color="text.secondary">
                    {item.percentual.toFixed(1)}%
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {/* Linha de total: fecha a tabela sem hairline sobrando embaixo. */}
            <TableRow sx={{ "& > td": { borderBottom: "none" } }}>
              <TableCell colSpan={2}>
                <Typography variant="labelMono" color="text.secondary">
                  Total
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="dataMono" sx={{ fontWeight: 600 }}>
                  <MoedaText value={rendimentosTotal} />
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography
                  variant="dataMono"
                  color="text.secondary"
                  sx={{ fontWeight: 600 }}
                >
                  100%
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Box mt={2}>
          {/* Nota corrida: `caption` virou rótulo mono UPPERCASE no sistema. */}
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            * Valores convertidos pela taxa de câmbio do dia do recebimento
          </Typography>
        </Box>
      </CardContent>
    </FelixCard>
  );
}
